import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import PresentationApplicationsData from '@/ts/PresentationApplicationsData';
import VacantUnitData from '@/ts/VacantUnitData';
import InProgressOfferData from '@/ts/InProgressOfferData';
import DeadlineOfferData from '@/ts/DeadlineOfferData';
import SentOfferData from '@/ts/SentOfferData';
import CompletedOfferData from '@/ts/CompletedOfferData';
import EditPresentationParticipantData from '@/ts/EditPresentationParticipantData';
import ClosedOfferData from '@/ts/ClosedOfferData';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';

@Module({name: 'PresentationFlows', namespaced: true})
export default class PresentationFlows extends VuexModule {
    public presentationType: string = 'all';
    public vacantUnits: VacantUnitData[] = [];
    public inprogressOffers: InProgressOfferData[] = [];
    public deadlineOffers: DeadlineOfferData[] = [];
    public sentOffers: SentOfferData[] = [];
    public completedOffers: CompletedOfferData[] = [];
    public closedOffers: ClosedOfferData[] = [];
    public currentEditPresentation: any = [];
    public currentEditPresentationParticipants: EditPresentationParticipantData[] = [];
    public currentVacantUnitAdd: any = [];
    public currentProject: any = 'all';
    public applicationsForPresentation: PresentationApplicationsData[] = [];
    public presentationHeader: string = 'Vacant units';
    public addPresentationVisibility: boolean = false;
    public matchApplications: boolean = true;
    public notActiveInOtherOffers: boolean = true;
    public previouslyOfferedUnit: boolean = false;
    public editPresentationVisibility: boolean = false;
    public finalizePresentationVisibility: boolean = false;
    public assignTenantVisibility: boolean = false;
    public searchQuery: string = '';

    @Mutation
    public RESET_PRESENTATION_FLOWS_STORE_STATE() {
        this.presentationType                    = 'all';
        this.vacantUnits                         = [];
        this.inprogressOffers                    = [];
        this.deadlineOffers                      = [];
        this.sentOffers                          = [];
        this.completedOffers                     = [];
        this.currentEditPresentation             = [];
        this.currentEditPresentationParticipants = [];
        this.currentVacantUnitAdd                = [];
        this.currentProject                      = 'all';
        this.applicationsForPresentation         = [];
        this.presentationHeader                  = 'Vacant units';
        this.addPresentationVisibility           = false;
        this.matchApplications                   = true;
        this.notActiveInOtherOffers              = true;
        this.previouslyOfferedUnit               = false;
        this.editPresentationVisibility          = false;
        this.finalizePresentationVisibility      = false;
        this.assignTenantVisibility              = false;
        this.searchQuery                         = '';
    }

    get getCurrentProject() {
        return this.currentProject;
    }

    get getVacantUnits(): VacantUnitData[] {
        return this.vacantUnits;
    }

    get getInprogressOffers(): InProgressOfferData[] {
        return this.inprogressOffers;
    }

    get getDeadlineOffers(): DeadlineOfferData[] {
        return this.deadlineOffers;
    }

    get getSentOffers(): SentOfferData[] {
        return this.sentOffers;
    }

    get getCompletedOffers(): CompletedOfferData[] {
        return this.completedOffers;
    }

    get getClosedOffers(): ClosedOfferData[] {
        return this.closedOffers;
    }

    get getCurrentVacantUnitAdd() {
        return this.currentVacantUnitAdd;
    }

    get getPresentationType() {
        return this.presentationType;
    }

    get getPresentationHeader() {
        return this.presentationHeader;
    }

    get getAddPresentationVisibility() {
        return this.addPresentationVisibility;
    }

    get getEditPresentationVisibility() {
        return this.editPresentationVisibility;
    }

    get getFinalizePresentationVisibility() {
        return this.finalizePresentationVisibility;
    }

    get getAssignTenantVisibility() {
        return this.assignTenantVisibility;
    }

    get getCurrentEditPresentation() {
        return this.currentEditPresentation;
    }
    get getCurrentEditPresentationParticipants(): EditPresentationParticipantData[] {
        return this.currentEditPresentationParticipants;
    }

    get getApplicationsForPresentation(): PresentationApplicationsData[] {
        return this.applicationsForPresentation;
    }

    get getMatchApplications() {
        return this.matchApplications;
    }

    get getNotActiveInOtherOffers() {
        return this.notActiveInOtherOffers;
    }

    get getPreviouslyOfferedUnit() {
        return this.previouslyOfferedUnit;
    }

    get getSearchQuery(): string {
        return this.searchQuery;
    }

    @Action
    public TOGGLE_PRESENTATION_FLOW_TAB(data: any) {
        store.commit(Mutations.SET_PRESENTATION_TYPE, data.option);
        store.commit(Mutations.SET_PRESENTATION_HEADER, data.title);
    }

    @Action
    public SORT_ICON_TOGGLE(data: any) {
        const filterIcons = document.querySelectorAll('.presentation-filter-icon');
        filterIcons.forEach( (filterIcon) => {
          filterIcon.classList.add('fa-angle-down');
          filterIcon.classList.remove('fa-angle-up');
          filterIcon.classList.remove('activeFilter');
        });
        if (data.direction === 'asc') {
          data.iconElement.classList.add('fa-angle-down');
          data.iconElement.classList.add('activeFilter');
          data.iconElement.classList.remove('fa-angle-up');
        } else {
          data.iconElement.classList.remove('fa-angle-down');
          data.iconElement.classList.add('fa-angle-up');
          data.iconElement.classList.add('activeFilter');
        }
    }

    @Action
    public ASSIGN_TENANT(data: any) {
        const requestData = {
            message: data.message,
            smsNotification: data.smsNotification,
            applicationId: data.applicationId,
            deadlineDate: data.deadlineDate,
            presentationId: data.presentationId,
        };

        axios.post(Endpoints.Admin.PresentationFlow.ASSIGN_TENANT, requestData)
            .then(() => {
                store.commit(Mutations.SET_ASSIGN_TENANT_VISIBILITY, false);
                store.dispatch(Actions.FETCH_ALL_DEADLINE_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
                NotifyHelpers.notify('tenant_has_been_assigned', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 400) {
                        NotifyHelpers.notify('unit_is_already_occupied', 'warning');
                    } else {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    }
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_VACANT_UNITS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.ALL_VACANT_UNITS, { params: {project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_VACANT_UNITS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_VACANT_UNIT(unitId: number) {
        axios.get(Endpoints.Admin.PresentationFlow.CURRENT_VACANT_UNIT(unitId))
            .then((response: any) => {
                store.commit(Mutations.SET_CURRENT_VACANT_UNIT_ADD, response.data);
                store.dispatch(Actions.FETCH_APPLICATIONS_FOR_PRESENTATION, {unitId , match: store.getters['PresentationFlows/getMatchApplications'], project: store.getters['PresentationFlows/getCurrentProject']});
                store.commit(Mutations.SET_ADD_PRESENTATION_VISIBILITY, true);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_APPLICATIONS_FOR_PRESENTATION(data: any) {
        this.applicationsForPresentation = [];

        axios.get(Endpoints.Admin.PresentationFlow.APPLICATIONS_FOR_PRESENTATION(
            data.unitId,
            data.match,
            data.project,
            data.notActive,
            data.previouslyOffered,
        ))
            .then((response: any) => {
                store.commit(Mutations.SET_APPLICATIONS_FOR_PRESENTATION, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_PRESENTATION_OFFER(data: any) {
        const requestData = {
            message: data.message,
            checkedIds: data.checkedIds,
            presentationDate: data.presentationDate,
            deadlineDate: data.deadlineDate,
            moveinDate: data.moveinDate,
            unitId: data.unitId,
            smsNotification: data.smsNotification,
            noPresentation: data.noPresentation,
        };

        axios.post(Endpoints.Admin.PresentationFlow.SEND_PRESENTATION_OFFER, requestData)
            .then((res) => {
                store.dispatch(Actions.FETCH_ALL_VACANT_UNITS, { project: store.getters['PresentationFlows/getCurrentProject'] });
                store.commit(Mutations.SET_ADD_PRESENTATION_VISIBILITY, false);
                NotifyHelpers.notify('presentation_has_been_created', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 400) {
                        NotifyHelpers.notify('unit_is_already_occupied', 'warning');
                    } else {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    }
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public PRESENTATION_DEADLINE_REACHED(presentationId: any) {
        axios.post(Endpoints.Admin.PresentationFlow.PRESENTATION_DEADLINE_REACHED(presentationId))
            .then(() => {
                store.commit(Mutations.SET_EDIT_PRESENTATION_VISIBILITY, false);
                store.dispatch(Actions.FETCH_ALL_INPROGRESS_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
                NotifyHelpers.notify('presentation_has_been_changed_as_deadline_reached', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_INPROGRESS_OFFERS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.ALL_IN_PROGRESS_OFFERS, { params: { project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_INPROGRESS_OFFERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_EDIT_PRESENTATION(presentationId: number) {
        axios.get(Endpoints.Admin.PresentationFlow.CURRENT_EDIT_PRESENTATION(presentationId))
            .then((response: any) => {
                store.commit(Mutations.SET_CURRENT_EDIT_PRESENTATION, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_PRESENTATION_EDIT_OFFER(data: any) {
        const requestData = {
            message: data.message,
            presentationId: data.presentationId,
            presentationDate: data.presentationDate,
            deadlineDate: data.deadlineDate,
            moveinDate: data.moveinDate,
        };

        axios.patch(Endpoints.Admin.PresentationFlow.SEND_PRESENTATION_EDIT_OFFER, requestData)
            .then(() => {
                store.commit(Mutations.SET_EDIT_PRESENTATION_VISIBILITY, false);
                store.dispatch(Actions.FETCH_ALL_INPROGRESS_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
                NotifyHelpers.notify('presentation_has_been_updated', 'positive');
            })
            .catch((error) => {
                store.commit(Mutations.SET_EDIT_PRESENTATION_VISIBILITY, false);

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_DEADLINE_OFFERS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.ALL_DEADLINE_OFFERS, { params: {project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_DEADLINE_OFFERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_COMPLETED_OFFERS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.ALL_COMPLETED_OFFERS, { params: {project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_COMPLETED_OFFERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_CLOSED_OFFERS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.CLOSED_OFFERS, { params: {project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_CLOSED_OFFERS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ALL_SENT_OFFERS(data: any) {
        axios.get(Endpoints.Admin.PresentationFlow.ALL_SENT_OFFERS, { params: {project: data.project} })
            .then((response: any) => {
                store.commit(Mutations.SET_SENT_OFFERS, response.data);
                store.commit(Mutations.SET_FINALIZE_PRESENTATION_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public RE_OPEN_OFFER(presentationId: number) {
        axios.post(Endpoints.Admin.PresentationFlow.RE_OPEN_OFFER(presentationId))
            .then((response: any) => {
                NotifyHelpers.notify('offer_has_been_re_open', 'positive');
                store.dispatch(Actions.FETCH_ALL_SENT_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public COMPLETE_OFFER(data: any) {
        const requestData = {
            id: data.presentationId,
            email: data.assignedEmail,
        };

        axios.post(Endpoints.Admin.PresentationFlow.COMPLETE_OFFER, requestData)
            .then((response: any) => {
                NotifyHelpers.notify('offer_has_been_completed', 'positive');
                store.dispatch(Actions.FETCH_ALL_SENT_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
            })
            .catch((error) => {
                if (error.response) {
                    if (error.response.status === 400) {
                        NotifyHelpers.notify('user_has_already_been_assigned_to_another_unit', 'warning');
                    } else if (error.response.status === 403) {
                        NotifyHelpers.notify('unit_is_already_occupied_please_reopen_and_close_the_offer', 'warning');
                    } else {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    }
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CLOSE_OFFER(presentationId: number) {
        axios.post(Endpoints.Admin.PresentationFlow.CLOSE_OFFER(presentationId))
            .then(() => {
                store.commit(Mutations.SET_ASSIGN_TENANT_VISIBILITY, false);
                store.dispatch(Actions.FETCH_ALL_DEADLINE_OFFERS, {project: store.getters['PresentationFlows/getCurrentProject']});
                NotifyHelpers.notify('offer_has_been_closed', 'warning');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public SET_PRESENTATION_TYPE(type: string) {
        this.presentationType = type;
    }

    @Mutation
    public SET_CURRENT_PROJECT(project: any) {
        this.currentProject = project;
    }

    @Mutation
    public SET_VACANT_UNITS(data: any) {
        this.vacantUnits = [];

        for (const vacantUnit of data) {
            this.vacantUnits.push(new VacantUnitData(
                vacantUnit.id,
                vacantUnit.name,
                vacantUnit.project,
                vacantUnit.address,
                vacantUnit.releaseDate,
                vacantUnit.propertyAvailable,
                vacantUnit.groups,
                vacantUnit.activePresentations,
                vacantUnit.type,
            ));
        }
    }

    @Mutation
    public SET_INPROGRESS_OFFERS(data: any) {
        this.inprogressOffers = [];

        for (const inProgressOffer of data) {
            this.inprogressOffers.push(new InProgressOfferData(
                inProgressOffer.id,
                inProgressOffer.project,
                inProgressOffer.sentDate,
                inProgressOffer.deadLineDate,
                inProgressOffer.moveInDate,
                inProgressOffer.unitId,
                inProgressOffer.propertyId,
                inProgressOffer.address,
                inProgressOffer.type,
                inProgressOffer.applicants,
                inProgressOffer.groups,
                inProgressOffer.createdBy,
            ));
        }
    }

    @Mutation
    public SET_DEADLINE_OFFERS(data: any) {
        this.deadlineOffers = [];

        for (const deadlineOffer of data) {
            this.deadlineOffers.push(new DeadlineOfferData(
                deadlineOffer.id,
                deadlineOffer.project,
                deadlineOffer.sentDate,
                deadlineOffer.deadLineDate,
                deadlineOffer.moveInDate,
                deadlineOffer.unitId,
                deadlineOffer.propertyId,
                deadlineOffer.address,
                deadlineOffer.type,
                deadlineOffer.applicants,
                deadlineOffer.groups,
                deadlineOffer.createdBy,
            ));
        }
    }

    @Mutation
    public SET_SENT_OFFERS(data: any) {
        this.sentOffers = [];

        for (const sentOffer of data) {
            this.sentOffers.push(new SentOfferData(
                sentOffer.id,
                sentOffer.project,
                sentOffer.sentDate,
                sentOffer.deadlineDate,
                sentOffer.moveInDate,
                sentOffer.unitId,
                sentOffer.propertyId,
                sentOffer.address,
                sentOffer.type,
                sentOffer.assignedTo,
                sentOffer.groups,
                sentOffer.createdBy,
            ));
        }
    }

    @Mutation
    public SET_COMPLETED_OFFERS(data: any) {
        this.completedOffers = [];

        for (const completeOffer of data) {
            this.completedOffers.push(new CompletedOfferData(
                completeOffer.id,
                completeOffer.name,
                completeOffer.sentDate,
                completeOffer.moveInDate,
                completeOffer.completedDate,
                completeOffer.unitId,
                completeOffer.propertyId,
                completeOffer.address,
                completeOffer.project,
                completeOffer.groups,
                completeOffer.createdBy,
                completeOffer.email,
                completeOffer.phone,
                completeOffer.completedBy,
                completeOffer.removedAt,
                completeOffer.updatedAt,
                completeOffer.type,
                completeOffer.closedAt,
            ));
        }
    }

    @Mutation
    public SET_CLOSED_OFFERS(data: any) {
        this.closedOffers = [];

        for (const closedOffer of data) {
            this.closedOffers.push(new ClosedOfferData(
                closedOffer.id,
                closedOffer.name,
                closedOffer.sentDate,
                closedOffer.moveInDate,
                closedOffer.completedDate,
                closedOffer.unitId,
                closedOffer.propertyId,
                closedOffer.address,
                closedOffer.project,
                closedOffer.groups,
                closedOffer.createdBy,
                closedOffer.email,
                closedOffer.phone,
                closedOffer.completedBy,
                closedOffer.removedAt,
                closedOffer.updatedAt,
                closedOffer.type,
                closedOffer.closedAt,
            ));
        }
    }

    @Mutation
    public SET_CURRENT_VACANT_UNIT_ADD(data: any) {
        this.currentVacantUnitAdd = data;
    }

    @Mutation
    public SET_PRESENTATION_HEADER(title: string) {
        this.presentationHeader = title;
    }

    @Mutation
    public SET_ADD_PRESENTATION_VISIBILITY(visibility: boolean) {
        this.addPresentationVisibility = visibility;
    }

    @Mutation
    public SET_EDIT_PRESENTATION_VISIBILITY(visibility: boolean) {
        this.editPresentationVisibility = visibility;
    }

    @Mutation
    public SET_ASSIGN_TENANT_VISIBILITY(visibility: boolean) {
        this.assignTenantVisibility = visibility;
    }

    @Mutation
    public SET_FINALIZE_PRESENTATION_VISIBILITY(visibility: boolean) {
        this.finalizePresentationVisibility = visibility;
    }

    @Mutation
    public SET_CURRENT_EDIT_PRESENTATION(data: any) {
        this.currentEditPresentationParticipants = [];
        this.currentEditPresentation = data;

        if (typeof data.participants === 'undefined') {
            return;
        }

        for (const participant of data.participants) {
            if (participant.application !== null) {
                this.currentEditPresentationParticipants.push(new EditPresentationParticipantData(
                    participant.application.id,
                    participant.application.created_at,
                    participant.application.interested_from,
                    participant.application.first_name,
                    participant.application.last_name,
                    participant.application.mail,
                    participant.application.phone,
                    participant.application.language,
                    participant.application.resident,
                    participant.rvsp_answer,
                ));
            }
        }
    }

    @Mutation
    public SET_APPLICATIONS_FOR_PRESENTATION(data: any) {
        this.applicationsForPresentation = [];

        for (const application of data) {
            this.applicationsForPresentation.push(new PresentationApplicationsData(
                application.id,
                application.first_name,
                application.last_name,
                application.mail,
                application.phone,
                application.language,
                application.resident,
                application.interested_from,
                application.created_at,
                application.gender,
                application.age,
                application.nationality,
                application.education_tags,
                application.interests_tags,
                application.about_me,
                application.application_motivation,
                application.other_interests,
                application.min_apartment_size,
                application.max_apartment_size,
                application.min_rent,
                application.max_rent,
                application.number_of_rooms,
                application.option_answers,
            ));
        }
    }

    @Mutation
    public RESET_ALL_MODAL_VISIBILITY() {
        this.addPresentationVisibility = false;
        this.editPresentationVisibility = false;
        this.assignTenantVisibility = false;
        this.finalizePresentationVisibility = false;
    }

    @Mutation
    public SET_APPLICATION_MATCH(match: any = null) {
        if (match === null) {
            this.matchApplications = !this.matchApplications;
        } else {
            this.matchApplications = match;
        }
    }

    @Mutation
    public SET_NOT_ACTIVE_IN_OTHER_OFFERS(status: boolean|null = null) {
        this.notActiveInOtherOffers = status === null ? false : status;
    }

    @Mutation
    public SET_PREVIOUSLY_OFFER_UNIT(status: boolean|null = null) {
        this.previouslyOfferedUnit = status === null ? false : status;
    }

    @Mutation
    public SET_PRESENTATIONS_SEARCH_QUERY(query: string) {
        this.searchQuery = query;
    }
}
